import type { DeadlineValiditySuggestionDto } from '@prospero/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { deadlinesQueryKeys } from '../../pages/documents-archive/deadlines/deadline-query-keys';
import { ConsultantDeadlinesService } from '../../services/consultant/consultant-deadlines.service';
import type { IConsultantDeadlinesService } from '../../services/consultant/consultant-deadlines.service.interface';
import { DeadlineService, type IDeadlineService } from '../../services/deadline/deadline.service';
import { consultantDeadlineQueryKeys } from '../consultant/use-consultant-deadlines';
import { useAuthUser } from '../use-auth-user';

export type ValiditySuggestionScope = 'tenant' | 'consultant';

const SUGGESTION_STALE_TIME_MS = 60_000;

export function useValiditySuggestion(
  scope: ValiditySuggestionScope,
  documentTypeId: string | null,
) {
  const tenantService = useInjection<IDeadlineService>(DeadlineService);
  const consultantService = useInjection<IConsultantDeadlinesService>(ConsultantDeadlinesService);
  const consultantUserId = useAuthUser()?.sub ?? '';
  const queryClient = useQueryClient();

  function buildOptions(typeId: string) {
    return {
      queryKey:
        scope === 'tenant'
          ? deadlinesQueryKeys.validitySuggestion(typeId)
          : consultantDeadlineQueryKeys.validitySuggestion(consultantUserId, typeId),
      queryFn: () =>
        scope === 'tenant'
          ? tenantService.getValiditySuggestion(typeId)
          : consultantService.getValiditySuggestion(typeId),
      staleTime: SUGGESTION_STALE_TIME_MS,
    };
  }

  const query = useQuery({
    ...buildOptions(documentTypeId ?? ''),
    enabled: documentTypeId !== null && documentTypeId !== '',
  });

  return {
    suggestion: query.data,
    isLoading: query.isLoading,
    /** Per il prefill nell'handler di cambio tipo: stessa cache della query. */
    fetchSuggestion: (typeId: string): Promise<DeadlineValiditySuggestionDto> =>
      queryClient.fetchQuery(buildOptions(typeId)),
  };
}
